import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { Storage } from 'react-jhipster';
import { BackgroundExportManager, isMobileDevice } from 'app/modules/reports/background-export-manager';
import logoImage from '../../../content/images/img.png';

export interface FeeReportCheckboxStates {
  quotationChecked: boolean;
  poChecked: boolean;
  dateChecked: boolean;
  customerChecked: boolean;
  bankChecked: boolean;
  branchChecked: boolean;
  bankUserChecked: boolean;
  feeAmountChecked: boolean;
  feeCurrencyChecked: boolean;
  statusChecked: boolean;
  amountTransferredChecked: boolean;
}

export interface FeeReportHelperFunctions {
  translateStatus: (q: any) => string;
  cancelledTransaction: (q: any) => string;
  getStaffFullName: (createdUser: any) => string;
  getAmountTransferredForExcel: (value: boolean) => string;
}

/**
 * Add English headers with logo for Fee Report
 */
export function addEnglishHeadersFeeReport(pdf: jsPDF, pageWidth: number, marginOuter: number, currentY: number): void {
  pdf.setFontSize(25);
  pdf.text('Fee Report', marginOuter, currentY + 8);

  const imgWidth = 75;
  const imgHeight = 10;

  try {
    pdf.addImage(logoImage, 'PNG', pageWidth - marginOuter - imgWidth, currentY, imgWidth, imgHeight, undefined, 'FAST');
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error);
  }
}

/**
 * Add Arabic headers with logo for Fee Report (RTL)
 */
export function addArabicHeadersFeeReport(
  pdf: jsPDF,
  pageWidth: number,
  marginOuter: number,
  currentY: number,
  fontName = 'Amiri-Regular'
): void {
  const imgWidth = 75;
  const imgHeight = 10;

  try {
    pdf.addImage(logoImage, 'PNG', marginOuter, currentY, imgWidth, imgHeight, undefined, 'FAST');
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error);
  }

  pdf.setFont(fontName, 'normal');
  pdf.setFontSize(25);

  const textWidth = pdf.getTextWidth('تقرير الرسوم');
  pdf.text('تقرير الرسوم', pageWidth - marginOuter - textWidth, currentY + 8);
}

/**
 * Add info sections for Fee Report (Order and Company/Bank info)
 */
export function addInfoSectionsFeeReport(
  pdf: jsPDF,
  isArabic: boolean,
  pageWidth: number,
  marginOuter: number,
  currentY: number,
  fontName?: string
): number {
  pdf.setFontSize(14);

  if (isArabic) {
    // Arabic info sections (RTL)
    if (fontName) pdf.setFont(fontName, 'normal');

    const leftColumnX = marginOuter + 75;
    const lineSpacing = 8;
    let localY = currentY;

    // Company info (left side for Arabic)
    let text = 'معلومات الشركة/البنك';
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${Storage.local.get('bank')} : الجهة`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${localStorage.getItem('branch')} : الفرع`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${localStorage.getItem('userName')} : المستخدم`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);

    // Order info (right side for Arabic)
    const rightColumnX = pageWidth - marginOuter;
    let orderInfoY = currentY;

    if (fontName) pdf.setFont(fontName, 'normal');
    text = 'طلب';
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);
    orderInfoY += lineSpacing;

    text = `${localStorage.getItem('period')} : فترة`;
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);

    return localY;
  } else {
    // English info sections (LTR)
    let localY = currentY;

    pdf.setFont(undefined, 'bold');
    pdf.text('Order', marginOuter, localY);
    pdf.setFont(undefined, 'normal');
    localY += 6;
    pdf.text(`Period : ${localStorage.getItem('period')}`, marginOuter, localY);

    const bankInfoX = pageWidth - marginOuter - 75;
    let bankInfoY = currentY;
    pdf.setFont(undefined, 'bold');
    pdf.text('Company/ Bank', bankInfoX, bankInfoY);
    pdf.setFont(undefined, 'normal');
    bankInfoY += 6;
    pdf.text(`Name : ${Storage.local.get('bank')}`, bankInfoX, bankInfoY);
    bankInfoY += 6;
    pdf.text(`Branch : ${localStorage.getItem('branch')}`, bankInfoX, bankInfoY);
    bankInfoY += 6;
    pdf.text(`User : ${localStorage.getItem('userName')}`, bankInfoX, bankInfoY);

    return localY;
  }
}

/**
 * Get table headers for Fee Report based on language and checkbox states
 */
export function getFeeReportTableHeaders(isArabic: boolean, checkboxStates: FeeReportCheckboxStates): string[][] {
  const {
    quotationChecked,
    poChecked,
    dateChecked,
    customerChecked,
    bankChecked,
    branchChecked,
    bankUserChecked,
    feeAmountChecked,
    feeCurrencyChecked,
    statusChecked,
    amountTransferredChecked,
  } = checkboxStates;

  if (isArabic) {
    return [
      [
        amountTransferredChecked ? 'المبلغ المحول' : '',
        statusChecked ? 'الحالة' : '',
        feeCurrencyChecked ? 'عملة الرسوم' : '',
        feeAmountChecked ? 'مبلغ الرسوم' : '',
        bankUserChecked ? 'مستخدم البنك' : '',
        branchChecked ? 'اسم الفرع' : '',
        bankChecked ? 'اسم البنك' : '',
        customerChecked ? 'العميل' : '',
        dateChecked ? 'التاريخ' : '',
        poChecked ? 'رقم المرجع' : '',
        quotationChecked ? 'رقم عرض السعر' : '',
      ],
    ];
  } else {
    return [
      [
        quotationChecked ? 'Quotation No' : '',
        poChecked ? 'Reference Number' : '',
        dateChecked ? 'Date' : '',
        customerChecked ? 'Customer Name' : '',
        bankChecked ? 'Bank Name' : '',
        branchChecked ? 'Branch Name' : '',
        bankUserChecked ? 'Bank User' : '',
        feeAmountChecked ? 'Fee Amount' : '',
        feeCurrencyChecked ? 'Fee Currency' : '',
        statusChecked ? 'Status' : '',
        amountTransferredChecked ? 'Amount Transferred' : '',
      ],
    ];
  }
}

/**
 * Process table data for Fee Report PDF
 */
export async function processFeeReportTableData(
  allData: any[],
  isArabic: boolean,
  checkboxStates: FeeReportCheckboxStates,
  helpers: FeeReportHelperFunctions
): Promise<any[]> {
  const exportManager = BackgroundExportManager.getInstance();
  const processedData: any[] = [];
  const PROCESS_BATCH_SIZE = isMobileDevice() ? 50 : 200;

  const {
    quotationChecked,
    poChecked,
    dateChecked,
    customerChecked,
    bankChecked,
    branchChecked,
    bankUserChecked,
    feeAmountChecked,
    feeCurrencyChecked,
    statusChecked,
    amountTransferredChecked,
  } = checkboxStates;

  const { translateStatus, cancelledTransaction, getStaffFullName, getAmountTransferredForExcel } = helpers;

  await exportManager.processInBatches(allData, PROCESS_BATCH_SIZE, batch => {
    batch.forEach(q => {
      const rowData = isArabic
        ? [
            amountTransferredChecked ? getAmountTransferredForExcel(q?.amountTransferred) : '',
            statusChecked ? translateStatus(q) : '',
            feeCurrencyChecked ? q?.feeCurrencyIndex || '' : '',
            feeAmountChecked ? cancelledTransaction(q) : '',
            bankUserChecked ? getStaffFullName(q?.createdUser) : '',
            branchChecked ? q?.branch?.name || '' : '',
            bankChecked ? q?.bank?.name || '' : '',
            customerChecked
              ? q?.commodity?.ownership === 'Local' && q?.customer?.nameArabic
                ? q?.customer?.nameArabic
                : q?.customer?.name
              : '',
            dateChecked ? moment(q?.createdDate).format('yyyy/MM/DD') : '',
            poChecked
              ? q?.bursaAPIBidCertId !== null
                ? q?.bursaAPIBidCert?.ecertNo
                : q?.commodity?.ownership === 'Local'
                ? q?.transactionRefNo || '-'
                : '-'
              : '',
            quotationChecked ? q?.id || '' : '',
          ]
        : [
            quotationChecked ? q?.id || '' : '',
            poChecked
              ? q?.bursaAPIBidCertId !== null
                ? q?.bursaAPIBidCert?.ecertNo
                : q?.commodity?.ownership === 'Local'
                ? q?.transactionRefNo || '-'
                : '-'
              : '',
            dateChecked ? moment(q?.createdDate).format('yyyy/MM/DD') : '',
            customerChecked
              ? q?.commodity?.ownership === 'Local' && q?.customer?.nameArabic
                ? q?.customer?.nameArabic
                : q?.customer?.name
              : '',
            bankChecked ? q?.bank?.name || '' : '',
            branchChecked ? q?.branch?.name || '' : '',
            bankUserChecked ? getStaffFullName(q?.createdUser) : '',
            feeAmountChecked ? cancelledTransaction(q) : '',
            feeCurrencyChecked ? q?.feeCurrencyIndex || '' : '',
            statusChecked ? translateStatus(q) : '',
            amountTransferredChecked ? getAmountTransferredForExcel(q?.amountTransferred) : '',
          ];

      processedData.push(rowData);
    });
  });

  return processedData;
}

/**
 * Get column styles for Fee Report table
 */
export function getFeeReportColumnStyles(isArabic: boolean): any {
  const baseStyles: any = {
    0: { cellWidth: 30 },
    1: { cellWidth: 30 },
    2: { cellWidth: 30 },
    3: { cellWidth: 20 },
    4: { cellWidth: 30 },
    5: { cellWidth: 30 },
    6: { cellWidth: 20 },
    7: { cellWidth: 20 },
    8: { cellWidth: 20 },
    9: { cellWidth: 20 },
    10: { cellWidth: 25 },
  };

  // Add RTL alignment for Arabic
  if (isArabic) {
    Object.keys(baseStyles).forEach(key => {
      baseStyles[key] = { ...baseStyles[key], halign: 'right' };
    });
  }

  return baseStyles;
}

/**
 * Add summary section for Fee Report at the end of PDF
 */
export function addFeeReportSummarySection(
  pdf: jsPDF,
  isArabic: boolean,
  pageWidth: number,
  marginOuter: number,
  currentY: number,
  translate: (key: string) => string,
  fontName?: string
): number {
  pdf.setFontSize(14);

  if (isArabic) {
    if (fontName) pdf.setFont(fontName, 'normal');
    const summaryText = 'ملخص / توقعات أداء الرسوم والأنشطة';
    pdf.text(summaryText, pageWidth - marginOuter - pdf.getTextWidth(summaryText), currentY);

    currentY += 5;
    pdf.setLineWidth(0.2);
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 10;
    const commentText = 'تعليقات على أداء الموظفين والعملاء';
    pdf.text(commentText, pageWidth - marginOuter - pdf.getTextWidth(commentText), currentY);

    currentY += 5;
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 30;
    pdf.setFontSize(14);
    const sigText = '  _________________________ :التوقيع ';
    pdf.text(sigText, pageWidth - marginOuter - pdf.getTextWidth(sigText), currentY);
  } else {
    pdf.text(translate('global.menu.report-print.summary'), marginOuter, currentY);
    pdf.setFont(undefined, 'normal');

    currentY += 5;
    pdf.setLineWidth(0.2);
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 10;
    pdf.text(translate('global.menu.report-print.comment'), marginOuter, currentY);
    pdf.setFont(undefined, 'normal');

    currentY += 5;
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 30;
    pdf.setFontSize(14);
    pdf.text(`${translate('global.menu.report-print.signature')} : _________________________`, marginOuter, currentY);
  }

  // Final divider
  currentY += 15;
  pdf.setDrawColor(1, 87, 155);
  pdf.setLineWidth(1);
  pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

  return currentY;
}

/**
 * Add page numbers to Fee Report PDF
 */
export function addFeeReportPageNumbers(pdf: jsPDF, isArabic: boolean, marginOuter: number): void {
  const pageCount = pdf.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(10);
    const pageHeight = pdf.internal.pageSize.height;

    if (isArabic) {
      pdf.setFont('Amiri-Regular', 'normal');

      const pageText = `صفحة ${i} من ${pageCount}`;

      const pageWidth = pdf.internal.pageSize.width;
      pdf.text(pageText, pageWidth - marginOuter, pageHeight - 5, { align: 'right' });

      pdf.setFont('helvetica', 'normal');
    } else {
      const pageText = `Page ${i} of ${pageCount}`;
      pdf.text(pageText, marginOuter, pageHeight - 5);
    }
  }
}
